import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Home, DollarSign, AlertCircle, TrendingUp, Users,
  Calendar, Wrench, Building2, ChevronRight
} from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend, Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

// ════════════════════════════════════════════════════════════════════
// Isometric Building SVG Component
// ════════════════════════════════════════════════════════════════════
const IsometricBuilding = ({ height = 60, width = 40, depth = 40, color = '#6366f1', roofColor, windows = [], x = 0, y = 0 }) => {
  const h = height;
  const w = width;
  const d = depth;
  const rc = roofColor || color;
  const originX = x;
  const originY = y;

  const topPts = [
    { x: 0, y: 0 },
    { x: w / 2, y: -d / 4 },
    { x: 0, y: -d / 2 },
    { x: -w / 2, y: -d / 4 },
  ];
  const frontPts = [
    { x: -w / 2, y: -d / 4 },
    { x: w / 2, y: -d / 4 },
    { x: w / 2, y: -d / 4 + h },
    { x: -w / 2, y: -d / 4 + h },
  ];
  const rightPts = [
    { x: w / 2, y: -d / 4 },
    { x: 0, y: 0 },
    { x: 0, y: h },
    { x: w / 2, y: -d / 4 + h },
  ];

  const toSvg = (pts) => pts.map(p => `${p.x + originX},${p.y + originY}`).join(' ');

  return (
    <g>
      <ellipse cx={originX} cy={originY + h + 4} rx={w * 0.6} ry={d * 0.25} fill="rgba(0,0,0,0.08)" />
      <polygon points={toSvg(rightPts)} fill={color} stroke="rgba(0,0,0,0.12)" strokeWidth="0.5"
        style={{ filter: 'brightness(0.85)' }} />
      <polygon points={toSvg(frontPts)} fill={color} stroke="rgba(0,0,0,0.12)" strokeWidth="0.5" />
      {windows.map((win, i) => {
        const cols = 2;
        const row = Math.floor(i / cols);
        const col = i % cols;
        const winW = w * 0.2;
        const winH = h * 0.12;
        const wx = -w / 2 + w * 0.15 + col * (w / 2);
        const wy = -d / 4 + h * 0.15 + row * (h * 0.2);
        return (
          <motion.rect key={i} x={originX + wx} y={originY + wy} width={winW} height={winH}
            fill={win.on ? '#fef08a' : '#1e293b'} rx={1}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.1 }}
            style={{ filter: win.on ? 'drop-shadow(0 0 3px rgba(254,240,138,0.6))' : 'none' }} />
        );
      })}
      <polygon points={toSvg(topPts)} fill={rc} stroke="rgba(0,0,0,0.1)" strokeWidth="0.5"
        style={{ filter: 'brightness(1.1)' }} />
      <polygon
        points={`${originX - w * 0.15},${originY - d * 0.2} ${originX + w * 0.1},${originY - d * 0.3} ${originX + w * 0.15},${originY - d * 0.25} ${originX - w * 0.1},${originY - d * 0.15}`}
        fill="rgba(255,255,255,0.15)" style={{ pointerEvents: 'none' }} />
    </g>
  );
};

// ════════════════════════════════════════════════════════════════════
// Isometric Asset Scene – centerpiece of the spatial map
// ════════════════════════════════════════════════════════════════════
const IsometricAssetScene = ({ assets = 0, overdue = 0, activeLeases = 0 }) => {
  const buildingColors = [
    '#6366f1', '#8b5cf6', '#a855f7', '#3b82f6', '#2563eb',
    '#06b6d4', '#0891b2', '#0d9488', '#14b8a6', '#10b981',
    '#f59e0b', '#f97316', '#ef4444', '#e11d48',
  ];

  const buildings = useMemo(() => {
    const count = Math.min(assets, 12);
    const arr = [];
    const cols = 4;
    const spacingX = 42;
    const spacingZ = 36;
    const startX = -(cols - 1) * spacingX / 2;
    for (let i = 0; i < count; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const bh = 30 + Math.sin(i * 1.5) * 18 + (i % 3) * 8;
      const bw = 24 + (i % 3) * 5;
      const bd = 24 + ((i * 3) % 4) * 5;
      const winCount = Math.floor(bh / 18) * 2;
      const windows = Array.from({ length: winCount }, () => ({ on: Math.random() > 0.3 }));
      arr.push({
        height: bh, width: bw, depth: bd,
        color: buildingColors[i % buildingColors.length],
        x: startX + col * spacingX + (row % 2) * spacingX / 2,
        y: -row * spacingZ * 0.5,
        windows,
        roofColor: buildingColors[(i + 2) % buildingColors.length],
      });
    }
    return arr;
  }, [assets]);

  const heatIntensity = Math.min(overdue / 10, 1);

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Pulsing heat glow behind the scene */}
      <motion.div className="absolute inset-0 rounded-full pointer-events-none"
        animate={{
          background: [
            `radial-gradient(ellipse at 50% 60%, rgba(239,68,68,${heatIntensity * 0.12}) 0%, transparent 60%)`,
            `radial-gradient(ellipse at 50% 60%, rgba(239,68,68,${heatIntensity * 0.22}) 0%, transparent 60%)`,
            `radial-gradient(ellipse at 50% 60%, rgba(239,68,68,${heatIntensity * 0.12}) 0%, transparent 60%)`,
          ]
        }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div className="absolute inset-0 rounded-full pointer-events-none"
        animate={{
          background: overdue > 0
            ? `radial-gradient(ellipse at 50% 80%, rgba(251,191,36,${Math.min(overdue / 20, 0.18)}) 0%, transparent 50%)`
            : 'transparent'
        }}
        transition={{ duration: 2 }}
      />

      {/* Isometric grid floor */}
      <svg className="absolute bottom-[12%] w-[85%] h-[35%]" viewBox="-200 0 400 100" preserveAspectRatio="xMidYMax meet">
        <defs>
          <pattern id="gridSm" width="26" height="13" patternUnits="userSpaceOnUse">
            <path d="M 26 0 L 0 0 L 13 6.5 L 26 13 L 0 13" fill="none" stroke="rgba(99,102,241,0.07)" strokeWidth="0.4" />
          </pattern>
        </defs>
        <rect x="-200" y="0" width="400" height="100" fill="url(#gridSm)" />
      </svg>

      {/* Buildings */}
      <svg className="relative z-10 w-[88%] h-[78%]" viewBox="-180 -20 360 180" preserveAspectRatio="xMidYMid meet">
        {buildings.map((b, i) => (
          <motion.g key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, type: 'spring', stiffness: 100 }}>
            <IsometricBuilding {...b} />
          </motion.g>
        ))}
        {assets === 0 && (
          <g>
            <rect x="-24" y="-4" width="48" height="8" fill="#e5e7eb" rx="2" stroke="#d1d5db" strokeWidth="1" />
            <text x="0" y="14" textAnchor="middle" fill="#9ca3af" fontSize="7">Add your first asset</text>
          </g>
        )}
      </svg>

      <motion.div className="absolute bottom-[6%] text-center pointer-events-none"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
        <span className="text-[9px] text-gray-400 font-medium tracking-wider uppercase">
          {assets > 0 ? `${assets} Assets · ${activeLeases} Active` : 'No assets yet'}
        </span>
      </motion.div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
// Metric Card – used in both desktop floating and mobile stacked layouts
// ════════════════════════════════════════════════════════════════════
const MetricCard = ({ title, value, icon: Icon, color, gradient, heatIntensity = 0, isMobile = false }) => {
  const glow = heatIntensity > 0
    ? `0 0 ${12 + heatIntensity * 20}px rgba(251,191,36,${heatIntensity * 0.25})`
    : 'none';

  return (
    <motion.div
      className={`bg-white/80 backdrop-blur-xl rounded-2xl shadow-md border ${
        heatIntensity > 0 ? 'border-amber-200/60' : 'border-white/40'
      } px-4 py-3 flex items-center gap-3 ${isMobile ? 'w-full' : ''}`}
      style={{ filter: glow, boxShadow: heatIntensity > 0 ? `0 4px 16px rgba(251,191,36,${heatIntensity * 0.15})` : undefined }}
      whileHover={{ scale: 1.02, y: -1 }}
      animate={isMobile ? {} : {
        y: [0, -3, 0],
        transition: { duration: 3 + Math.random() * 2, repeat: Infinity, ease: 'easeInOut' }
      }}
    >
      <div className={`p-2 rounded-xl flex-shrink-0 ${gradient || 'bg-indigo-50'}`}>
        <Icon className={`h-5 w-5 ${color || 'text-indigo-600'}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] sm:text-[11px] text-gray-500 font-medium uppercase tracking-wider truncate">{title}</p>
        <p className="text-base sm:text-lg font-bold text-gray-900 mt-0.5 truncate">{value}</p>
      </div>
    </motion.div>
  );
};

// ════════════════════════════════════════════════════════════════════
// Heat Map Zone decorative overlay
// ════════════════════════════════════════════════════════════════════
const HeatMapZone = ({ intensity = 0, x = 50, y = 50, size = 30 }) => {
  if (intensity === 0) return null;
  return (
    <motion.div className="absolute pointer-events-none" style={{ left: `${x}%`, top: `${y}%`, width: `${size}%`, height: `${size}%`, transform: 'translate(-50%, -50%)' }}
      animate={{
        background: [
          `radial-gradient(ellipse at center, rgba(251,191,36,${intensity * 0.06}) 0%, transparent 70%)`,
          `radial-gradient(ellipse at center, rgba(239,68,68,${intensity * 0.10}) 0%, transparent 70%)`,
          `radial-gradient(ellipse at center, rgba(251,191,36,${intensity * 0.06}) 0%, transparent 70%)`,
        ]
      }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
    />
  );
};

// ════════════════════════════════════════════════════════════════════
// Main Dashboard Component
// ════════════════════════════════════════════════════════════════════
const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const [metricsRes, activitiesRes, revenueRes] = await Promise.all([
        api.get('/tenants/stats/dashboard'),
        api.get('/leases?limit=5'),
        api.get('/payments/revenue?group_by=month')
      ]);
      setMetrics(metricsRes.data.data);
      setRecentActivities(activitiesRes.data.data.data || []);
      if (revenueRes.data.success && revenueRes.data.data.revenue_by_period) {
        setRevenueData(revenueRes.data.data.revenue_by_period);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  // ── Derived data ──────────────────────────────────────────────
  const chartLabels = revenueData.map(item => {
    const [year, month] = item.period.split('-');
    return new Date(parseInt(year), parseInt(month) - 1)
      .toLocaleString('default', { month: 'short', year: 'numeric' });
  });
  const chartValues = revenueData.map(item => parseFloat(item.total_revenue));

  const revenueChartData = {
    labels: chartLabels.length > 0 ? chartLabels : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'Revenue (KES)',
      data: chartValues.length > 0 ? chartValues : [0, 0, 0, 0, 0, 0],
      borderColor: '#6366f1',
      backgroundColor: 'rgba(99,102,241,0.06)',
      fill: true, tension: 0.45,
      pointBackgroundColor: '#6366f1',
      pointBorderColor: '#fff', pointBorderWidth: 2, pointRadius: 3, pointHoverRadius: 5,
    }]
  };
  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.9)', padding: 10, titleColor: '#e2e8f0', bodyColor: '#f8fafc', cornerRadius: 8,
        callbacks: { label: (ctx) => 'KES ' + ctx.parsed.y.toLocaleString() }
      }
    },
    scales: {
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)', drawBorder: false }, ticks: { callback: (v) => 'KES ' + v.toLocaleString(), color: '#94a3b8', font: { size: 10 } } },
      x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { size: 10 } } }
    },
    interaction: { intersect: false, mode: 'index' }
  };

  const totalRevenue = revenueData.reduce((sum, item) => sum + parseFloat(item.total_revenue), 0);
  const averageRevenue = revenueData.length > 0 ? totalRevenue / revenueData.length : 0;
  const occupancyRate = metrics?.total_units > 0 ? Math.round((metrics.rented_units / metrics.total_units) * 100) : 0;
  const overdue = metrics?.overdue_payments || 0;
  const heatIntensity = Math.min(overdue / 15, 1);

  // ── Metric data ───────────────────────────────────────────────
  const metricsList = [
    { title: 'Total Assets', value: metrics?.total_assets || 0, icon: Building2, color: 'text-indigo-600', gradient: 'bg-indigo-50', heat: 0 },
    { title: 'Occupancy', value: `${occupancyRate}%`, icon: TrendingUp, color: 'text-emerald-600', gradient: 'bg-emerald-50', heat: 0 },
    { title: 'Monthly Revenue', value: `KES ${(metrics?.monthly_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'text-violet-600', gradient: 'bg-violet-50', heat: 0 },
    { title: 'Active Tenants', value: metrics?.total_tenants || 0, icon: Users, color: 'text-sky-600', gradient: 'bg-sky-50', heat: 0 },
    { title: 'Active Leases', value: metrics?.active_leases || 0, icon: Calendar, color: 'text-amber-600', gradient: 'bg-amber-50', heat: 0 },
    { title: 'Maintenance', value: metrics?.open_maintenance || 0, icon: Wrench, color: 'text-rose-600', gradient: 'bg-rose-50', heat: 0 },
    { title: 'Overdue Payments', value: overdue, icon: AlertCircle, color: 'text-orange-600', gradient: 'bg-orange-50', heat: heatIntensity },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-slate-50 -m-4 sm:-m-6 p-3 sm:p-6">
      {/* ── Header ─────────────────────────────────────────────── */}
      <motion.div className="mb-3 sm:mb-4" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-xs sm:text-sm text-gray-500">Your property ecosystem at a glance</p>
        </div>
      </motion.div>

      {/* ═══════════════════════════════════════════════════════════
          MOBILE LAYOUT (below lg breakpoint)
          ═══════════════════════════════════════════════════════════ */}
      <div className="lg:hidden space-y-3">
        {/* – Spatial Map (compact) – */}
        <motion.div className="relative bg-white/50 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 overflow-hidden"
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ minHeight: '220px' }}>
          <AnimatePresence>
            {overdue > 0 && (
              <motion.div className="absolute inset-0 pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] rounded-full"
                  animate={{
                    background: [
                      `radial-gradient(ellipse, rgba(251,191,36,${Math.min(overdue / 30, 0.12)}) 0%, transparent 60%)`,
                      `radial-gradient(ellipse, rgba(239,68,68,${Math.min(overdue / 25, 0.10)}) 0%, transparent 60%)`,
                      `radial-gradient(ellipse, rgba(251,191,36,${Math.min(overdue / 30, 0.12)}) 0%, transparent 60%)`,
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
              </motion.div>
            )}
          </AnimatePresence>
          <div className="h-[220px]">
            <IsometricAssetScene assets={metrics?.total_assets || 0} overdue={overdue} activeLeases={metrics?.active_leases || 0} />
          </div>
          {/* Ambient particles */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[1, 2, 3].map(i => (
              <motion.div key={i} className="absolute w-1 h-1 rounded-full bg-indigo-300/30"
                style={{ left: `${15 + i * 30}%`, top: `${20 + (i * 17) % 60}%` }}
                animate={{ y: [0, -10, 0], opacity: [0.2, 0.5, 0.2] }}
                transition={{ duration: 3 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }} />
            ))}
          </div>
        </motion.div>

        {/* – Metrics Grid (3 columns on mobile → 4 on tablet) – */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {metricsList.map((m, i) => (
            <motion.div key={m.title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.04 }}>
              <MetricCard {...m} isMobile />
            </motion.div>
          ))}
        </div>

        {/* – Revenue & Activities – */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <motion.div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 p-4"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5 text-indigo-500" /> Revenue
              </h3>
              <span className="text-[10px] text-gray-500">KES {totalRevenue.toLocaleString()}</span>
            </div>
            <div className="h-36">
              {revenueData.length > 0 ? <Line data={revenueChartData} options={chartOptions} />
                : <div className="flex items-center justify-center h-full text-gray-400 text-xs">No data</div>}
            </div>
          </motion.div>

          <motion.div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 p-4"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-indigo-500" /> Activities
            </h3>
            <div className="space-y-0.5 max-h-36 overflow-y-auto">
              {recentActivities.length > 0 ? recentActivities.slice(0, 4).map((a, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-indigo-50/50">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`p-1 rounded-lg flex-shrink-0 ${a.status === 'active' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                      <Home className={`h-3 w-3 ${a.status === 'active' ? 'text-emerald-500' : 'text-amber-500'}`} />
                    </div>
                    <p className="text-xs text-gray-700 truncate">{a.asset_name || a.first_name}</p>
                  </div>
                  <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">{new Date(a.created_at).toLocaleDateString()}</span>
                </div>
              )) : <div className="text-center py-4 text-gray-400 text-xs">No recent activities</div>}
            </div>
          </motion.div>
        </div>

        {/* Payment methods */}
        {metrics?.payment_methods && (
          <motion.div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 p-4"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Payment Methods</h3>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(metrics.payment_methods).map(([method, count]) => (
                <div key={method} className="text-center p-2 bg-white/60 rounded-xl border border-gray-100">
                  <p className="text-base font-bold text-indigo-600">{count}</p>
                  <p className="text-[10px] text-gray-500 capitalize break-words">{method.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          DESKTOP LAYOUT (lg and above) – Spatial Floating Layout
          ═══════════════════════════════════════════════════════════ */}
      <div className="hidden lg:block">
        {/* – Spatial Map Section with Floating Cards – */}
        <motion.div className="relative bg-white/50 backdrop-blur-sm rounded-3xl shadow-sm border border-white/60 mb-5 overflow-visible px-4 py-2"
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ minHeight: '420px' }}>
          <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none" style={{ zIndex: -1 }}>
            {/* Heat zones */}
            <HeatMapZone intensity={overdue * 0.04} x={55} y={40} size={35} />
            <HeatMapZone intensity={(metrics?.open_maintenance || 0) * 0.05} x={45} y={60} size={25} />
            <AnimatePresence>
              {overdue > 0 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50%] h-[60%] rounded-full"
                    animate={{
                      background: [
                        `radial-gradient(ellipse at 50% 60%, rgba(251,191,36,${Math.min(overdue / 30, 0.15)}) 0%, transparent 60%)`,
                        `radial-gradient(ellipse at 50% 60%, rgba(239,68,68,${Math.min(overdue / 25, 0.12)}) 0%, transparent 60%)`,
                        `radial-gradient(ellipse at 50% 60%, rgba(251,191,36,${Math.min(overdue / 30, 0.15)}) 0%, transparent 60%)`,
                      ]
                    }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Desktop floating metrics - radially arranged around center */}
          {/* Total Assets - Top center */}
          <FloatingCard {...metricsList[0]} pos={{ x: 50, y: 4 }} delay={0.1} />
          {/* Occupancy - Top left */}
          <FloatingCard {...metricsList[1]} pos={{ x: 22, y: 22 }} delay={0.15} />
          {/* Monthly Revenue - Top right */}
          <FloatingCard {...metricsList[2]} pos={{ x: 78, y: 22 }} delay={0.2} />
          {/* Active Tenants - Mid left */}
          <FloatingCard {...metricsList[3]} pos={{ x: 22, y: 50 }} delay={0.25} />
          {/* Active Leases - Mid right */}
          <FloatingCard {...metricsList[4]} pos={{ x: 78, y: 50 }} delay={0.3} />
          {/* Maintenance - Bottom left */}
          <FloatingCard {...metricsList[5]} pos={{ x: 23, y: 78 }} delay={0.35} />
          {/* Overdue Payments - Bottom right */}
          <FloatingCard {...metricsList[6]} pos={{ x: 77, y: 78 }} delay={0.4} />

          {/* Center: Isometric scene */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-full max-w-lg h-full pointer-events-auto">
              <IsometricAssetScene assets={metrics?.total_assets || 0} overdue={overdue} activeLeases={metrics?.active_leases || 0} />
            </div>
          </div>

          {/* Ambient particles */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[1, 2, 3, 4].map(i => (
              <motion.div key={i} className="absolute w-1 h-1 rounded-full bg-indigo-300/30"
                style={{ left: `${18 + i * 20}%`, top: `${12 + (i * 11) % 75}%` }}
                animate={{ y: [0, -14, 0], opacity: [0.2, 0.5, 0.2] }}
                transition={{ duration: 3 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }} />
            ))}
          </div>
        </motion.div>

        {/* – Bottom Row – */}
        <div className="grid grid-cols-2 gap-5">
          <motion.div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 p-5"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-indigo-500" /> Revenue Trend
              </h3>
              <div className="text-xs text-gray-500">
                Total: <strong className="text-indigo-600">KES {totalRevenue.toLocaleString()}</strong>
                <span className="text-gray-300 mx-2">|</span>
                Avg: <strong className="text-gray-700">KES {Math.round(averageRevenue).toLocaleString()}</strong>
              </div>
            </div>
            <div className="h-52">
              {revenueData.length > 0 ? <Line data={revenueChartData} options={chartOptions} />
                : <div className="flex items-center justify-center h-full text-gray-400 text-sm">No revenue data available yet</div>}
            </div>
          </motion.div>

          <motion.div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 p-5"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-indigo-500" /> Recent Activities
            </h3>
            <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
              {recentActivities.length > 0 ? recentActivities.map((a, i) => (
                <motion.div key={i} className="flex items-center justify-between py-2.5 px-3 rounded-xl hover:bg-indigo-50/50 group cursor-pointer"
                  initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.05 }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${a.status === 'active' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                      <Home className={`h-3.5 w-3.5 ${a.status === 'active' ? 'text-emerald-500' : 'text-amber-500'}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{a.asset_name || 'Property'}</p>
                      <p className="text-xs text-gray-500 truncate">{a.first_name} {a.last_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${a.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{a.status}</span>
                    <ChevronRight className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </motion.div>
              )) : <div className="text-center py-8 text-gray-400 text-sm">No recent activities</div>}
            </div>
          </motion.div>
        </div>

        {metrics?.payment_methods && (
          <motion.div className="mt-5 bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/60 p-5"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <h3 className="text-base font-semibold text-gray-800 mb-4">Payment Methods</h3>
            <div className="grid grid-cols-4 gap-3">
              {Object.entries(metrics.payment_methods).map(([method, count], i) => (
                <motion.div key={method} className="text-center p-3 bg-white/60 rounded-xl border border-gray-100"
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.05 }}
                  whileHover={{ y: -2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  <p className="text-xl font-bold text-indigo-600">{count}</p>
                  <p className="text-xs text-gray-500 capitalize break-words">{method.replace('_', ' ')}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ─── Small helper: FloatingCard used only on desktop ─────────────────
// Uses inset shadows so cards look recessed into the dashboard surface
const FloatingCard = ({ title, value, icon: Icon, color, gradient, heat = 0, pos, delay }) => {
  const glow = heat > 0
    ? `0 0 ${14 + heat * 20}px rgba(251,191,36,${heat * 0.25})`
    : 'none';

  return (
    <motion.div
      className="absolute z-20 cursor-pointer"
      style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: 'translate(-50%, 0)', filter: glow }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1, transition: { delay, type: 'spring', stiffness: 70, damping: 12 } }}
      whileHover={{ scale: 1.06, transition: { type: 'spring', stiffness: 300 } }}
    >
      <motion.div
        className={`rounded-xl border px-3 py-2 flex items-center gap-2 ${
          heat > 0 ? 'border-amber-400/30' : 'border-indigo-300/25'
        }`}
        style={{
          background: heat > 0
            ? 'linear-gradient(135deg, rgba(251,191,36,0.04), rgba(251,191,36,0.08))'
            : 'linear-gradient(135deg, rgba(99,102,241,0.03), rgba(139,92,246,0.06))',
          backdropFilter: 'blur(2px)',
        }}
        animate={{
          y: [0, -2, 0],
          boxShadow: [
            `inset 0 2px 6px rgba(0,0,0,${heat > 0 ? 0.08 : 0.05}), inset 0 -1px 2px rgba(255,255,255,0.4)`,
            `inset 0 4px 10px rgba(0,0,0,${heat > 0 ? 0.12 : 0.08}), inset 0 -1px 2px rgba(255,255,255,0.4)`,
            `inset 0 2px 6px rgba(0,0,0,${heat > 0 ? 0.08 : 0.05}), inset 0 -1px 2px rgba(255,255,255,0.4)`,
          ],
          borderColor: heat > 0
            ? ['rgba(251,191,36,0.25)', 'rgba(251,191,36,0.35)', 'rgba(251,191,36,0.25)']
            : ['rgba(99,102,241,0.18)', 'rgba(99,102,241,0.28)', 'rgba(99,102,241,0.18)'],
        }}
        transition={{
          y: { duration: 3 + delay, repeat: Infinity, ease: 'easeInOut', delay: delay * 0.5 },
          boxShadow: { duration: 3 + delay, repeat: Infinity, ease: 'easeInOut' },
          borderColor: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
        }}
      >
        <div
          className={`p-1.5 rounded-lg ${gradient || 'bg-indigo-50'}`}
          style={{ boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(255,255,255,0.8)' }}
        >
          <Icon className={`h-4 w-4 ${color || 'text-indigo-600'}`} />
        </div>
        <div className="min-w-0 max-w-[100px]">
          <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider truncate">{title}</p>
          <p className="text-sm font-bold text-gray-900 mt-0.5 truncate">{value}</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Dashboard;
